import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Loader } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import './AddMosque.scss';

const AddMosque = () => {
    const navigate = useNavigate();
    const { success, error, confirm } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        district: '',
        state: 'Kerala',
        dish: '',
        googleMapsLink: '',
        location: {
            latitude: '',
            longitude: ''
        },
        description: '',
        keywords: '',
        status: 'available',
        verified: false
    });
    const [loading, setLoading] = useState(false);
    const [extractingCoords, setExtractingCoords] = useState(false);

    const dishes = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];
    const districts = [
        'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod',
        'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad',
        'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
    ];

    const handleExtractCoordinates = async () => {
        const link = formData.googleMapsLink?.trim();
        
        if (!link || link === '') {
            error('Please enter a Google Maps link first');
            return;
        }

        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            error('Please enter a valid URL starting with http:// or https://');
            return;
        }

        setExtractingCoords(true);
        try {
            const response = await api.post('/admin/mosques/extract-coordinates', {
                googleMapsLink: link
            });
            
            if (response.data.success && response.data.coordinates) {
                setFormData(prev => ({
                    ...prev,
                    location: {
                        latitude: response.data.coordinates.latitude,
                        longitude: response.data.coordinates.longitude
                    }
                }));
                success(`Coordinates extracted: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
            } else {
                error('Could not extract coordinates from the URL. Please check the link format.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error extracting coordinates';
            error(errorMessage);
            console.error('Extract coordinates error:', err.response?.data || err);
        } finally {
            setExtractingCoords(false);
        }
    };

    // Keep the simple extraction for auto-fill on paste (basic formats only)
    const extractCoordsFromGoogleMaps = (url) => {
        try {
            const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
            const match = url.match(regex);
            if (match) {
                return {
                    latitude: parseFloat(match[1]),
                    longitude: parseFloat(match[2])
                };
            }
            const placeRegex = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
            const placeMatch = url.match(placeRegex);
            if (placeMatch) {
                return {
                    latitude: parseFloat(placeMatch[1]),
                    longitude: parseFloat(placeMatch[2])
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'googleMapsLink') {
            const coords = extractCoordsFromGoogleMaps(value);
            setFormData(prev => ({
                ...prev,
                googleMapsLink: value,
                location: coords || prev.location
            }));
        } else if (name.includes('location.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value
                }
            }));
        } else if (name === 'keywords') {
            setFormData(prev => ({
                ...prev,
                keywords: value.split(',').map(k => k.trim()).filter(k => k)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const submitMosque = async (forceConfirmDuplicate = false) => {
        const payload = { ...formData, forceConfirmDuplicate };
        const response = await api.post('/admin/mosques', payload);
        if (response.data.success) {
            success('Mosque added successfully!');
            navigate(`/mosques/${response.data.mosque.mosqueID}`);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await submitMosque(false);
        } catch (err) {
            const data = err.response?.data;
            if (data?.duplicateCheck && data?.requiresConfirmation && data?.nearbyMosques?.length > 0) {
                const names = data.nearbyMosques.map(m => `"${m.name}" (${m.mosqueID})`).join(', ');
                confirm(
                    `A mosque already exists within 200m of this location: ${names}. Add anyway?`,
                    async () => {
                        try {
                            await submitMosque(true);
                        } catch (retryErr) {
                            error(retryErr.response?.data?.message || 'Error adding mosque');
                        } finally {
                            setLoading(false);
                        }
                    },
                    () => setLoading(false)
                );
                return;
            }
            error(data?.message || 'Error adding mosque');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-mosque">
            <div className="page-header">
                <button className="back-button" onClick={() => navigate('/mosques')}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <h2>Add New Mosque</h2>
            </div>

            <form className="mosque-form" onSubmit={handleSubmit}>

                <div className="form-row">
                    <div className="form-group">
                        <label>Mosque Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>District *</label>
                        <select
                            name="district"
                            value={formData.district}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select District</option>
                            {districts.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>State *</label>
                        <input
                            type="text"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Food Type *</label>
                        <select
                            name="dish"
                            value={formData.dish}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select Food Type</option>
                            {dishes.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Google Maps Link *</label>
                    <div className="input-with-button">
                        <input
                            type="url"
                            name="googleMapsLink"
                            value={formData.googleMapsLink}
                            onChange={handleChange}
                            required
                            placeholder="https://www.google.com/maps/... or https://g.co/kgs/..."
                        />
                        <button
                            type="button"
                            className="extract-coords-btn"
                            onClick={handleExtractCoordinates}
                            disabled={extractingCoords || !formData.googleMapsLink}
                            title="Extract coordinates from URL"
                        >
                            {extractingCoords ? (
                                <Loader size={16} className="spinning" />
                            ) : (
                                <>
                                    <MapPin size={16} />
                                    Extract
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Latitude</label>
                        <input
                            type="number"
                            name="location.latitude"
                            value={formData.location.latitude}
                            onChange={handleChange}
                            step="any"
                        />
                    </div>

                    <div className="form-group">
                        <label>Longitude</label>
                        <input
                            type="number"
                            name="location.longitude"
                            value={formData.location.longitude}
                            onChange={handleChange}
                            step="any"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows="4"
                    />
                </div>

                <div className="form-group">
                    <label>Keywords (comma-separated)</label>
                    <input
                        type="text"
                        name="keywords"
                        value={formData.keywords.join(', ')}
                        onChange={handleChange}
                        placeholder="keyword1, keyword2, keyword3"
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                        >
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="verified"
                                checked={formData.verified}
                                onChange={handleChange}
                            />
                            Verified
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/mosques')}>
                        Cancel
                    </button>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Adding...' : 'Add Mosque'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddMosque;
